'use client';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Users,
  Crown,
  // Shield,
  // Edit3,
  // Eye,
  ChevronRight,
  ChevronLeft,
  UserPlus,
  Search,
  Check,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import socketService from '@/lib/socket';
import {
  fetchGroupUsers,
  GroupUserWithRole,
} from '@/services/groupChatService';
import { apiGet, apiPost } from '@/lib/api';
import { useAuthStore } from '@/stores';
import { showToast } from './Toast';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';

const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};

interface GroupMemberListProps {
  conversationId: string;
  isGroupChat: boolean;
  projectId?: string | null;
}

interface ProjectUser {
  id: string;
  name: string;
  email: string;
}

interface OnlineUser {
  id: number;
  name?: string;
  email?: string;
}

// 角色图标和颜色配置
// const roleConfig = {
//   owner: {
//     icon: Crown,
//     color: 'text-amber-500',
//     bgColor: 'bg-amber-50',
//     label: 'Owner',
//   },
//   manager: {
//     icon: Shield,
//     color: 'text-blue-500',
//     bgColor: 'bg-blue-50',
//     label: 'Manager',
//   },
//   editor: {
//     icon: Edit3,
//     color: 'text-green-500',
//     bgColor: 'bg-green-50',
//     label: 'Editor',
//   },
//   viewer: {
//     icon: Eye,
//     color: 'text-gray-500',
//     bgColor: 'bg-gray-50',
//     label: 'Viewer',
//   },
// };

export default function GroupMemberList({
  conversationId,
  isGroupChat,
  projectId,
}: GroupMemberListProps) {
  const [members, setMembers] = useState<GroupUserWithRole[]>([]);
  const [onlineUserIds, setOnlineUserIds] = useState<Set<number>>(new Set());
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { t } = useTranslation();
  useForceUpdate();

  // 🔥 Add member modal states
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [projectUsers, setProjectUsers] = useState<ProjectUser[]>([]);
  const [filteredProjectUsers, setFilteredProjectUsers] = useState<
    ProjectUser[]
  >([]);
  const [selectedNewUsers, setSelectedNewUsers] = useState<Set<string>>(
    new Set()
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingProjectUsers, setIsLoadingProjectUsers] = useState(false);
  const [isAddingMembers, setIsAddingMembers] = useState(false);
  const hasLoadedProjectUsersRef = useRef(false);

  // Get current user info
  const { userInfo } = useAuthStore();
  const currentUserId = userInfo?.id;

  // 获取用户首字母
  const getUserInitial = (user: { name?: string; email?: string }) => {
    if (user.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // 获取头像背景色（基于用户ID生成一致的颜色）
  // const getAvatarColor = (userId: number) => {
  //   const colors = [
  //     'bg-blue-500',
  //     'bg-green-500',
  //     'bg-purple-500',
  //     'bg-pink-500',
  //     'bg-indigo-500',
  //     'bg-teal-500',
  //     'bg-orange-500',
  //     'bg-cyan-500',
  //   ];
  //   return colors[userId % colors.length];
  // };

  // 加载群成员
  const loadMembers = useCallback(async () => {
    if (!conversationId || !isGroupChat) return;

    setIsLoading(true);
    try {
      const users = await fetchGroupUsers(conversationId);
      // 按角色排序：owner > manager > editor > viewer
      const roleOrder = { owner: 0, manager: 1, editor: 2, viewer: 3 };
      const sortedUsers = [...users].sort(
        (a, b) => roleOrder[a.role] - roleOrder[b.role]
      );
      setMembers(sortedUsers);
    } catch (error) {
      console.error('Failed to load group members:', error);
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, isGroupChat]);

  // 🔥 加载项目成员
  const loadProjectUsers = useCallback(async () => {
    if (!projectId || hasLoadedProjectUsersRef.current) return;

    setIsLoadingProjectUsers(true);
    try {
      const url = `/api/projects/${projectId}/users`;
      const response = await apiGet<any>(url);
      const data = response?.data || response || {};
      if (data && data.users) {
        // 转换 API 响应格式并排除当前登录用户
        const allUsers: ProjectUser[] = data.users.map((item: any) => ({
          id: String(item.user.id),
          name: item.user.name,
          email: item.user.email,
        }));
        const filteredOutCurrentUser = currentUserId
          ? allUsers.filter((user: ProjectUser) => {
              const userId = String(user.id);
              const currentId = String(currentUserId);
              return userId !== currentId;
            })
          : allUsers;
        setProjectUsers(filteredOutCurrentUser);
        setFilteredProjectUsers(filteredOutCurrentUser);
        hasLoadedProjectUsersRef.current = true;
      } else {
        setProjectUsers([]);
        setFilteredProjectUsers([]);
        hasLoadedProjectUsersRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching project members:', error);
      showToast(t('errors.failedToLoadProjectMembers'), 'error');
    } finally {
      setIsLoadingProjectUsers(false);
    }
  }, [projectId, currentUserId, t]);

  // 🔥 过滤项目成员（搜索）
  const filterProjectUsers = useCallback(
    (search: string) => {
      const trimmedSearch = search.trim().toLowerCase();
      if (trimmedSearch) {
        setFilteredProjectUsers(
          projectUsers.filter(
            user =>
              user.name.toLowerCase().includes(trimmedSearch) ||
              user.email.toLowerCase().includes(trimmedSearch)
          )
        );
      } else {
        setFilteredProjectUsers(projectUsers);
      }
    },
    [projectUsers]
  );

  // 🔥 搜索防抖
  useEffect(() => {
    if (!hasLoadedProjectUsersRef.current) return;
    const debounceTimer = setTimeout(() => {
      filterProjectUsers(searchQuery);
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, filterProjectUsers]);

  // 🔥 打开 Add Member 弹窗时加载项目成员
  useEffect(() => {
    if (showAddMemberModal) {
      hasLoadedProjectUsersRef.current = false;
      setSelectedNewUsers(new Set());
      setSearchQuery('');
      loadProjectUsers();
    }
  }, [showAddMemberModal, loadProjectUsers]);

  // 🔥 获取已在群聊中的用户ID集合
  const existingMemberIds = useMemo(
    () => new Set(members.map(m => String(m.user.id))),
    [members]
  );

  // 🔥 切换用户选择状态（仅限非已有成员）
  const toggleUserSelection = useCallback(
    (userId: string) => {
      if (existingMemberIds.has(userId)) return; // 已有成员不可取消
      setSelectedNewUsers(prev => {
        const newSet = new Set(prev);
        if (newSet.has(userId)) {
          newSet.delete(userId);
        } else {
          newSet.add(userId);
        }
        return newSet;
      });
    },
    [existingMemberIds]
  );

  // 🔥 添加成员到群聊
  const handleAddMembers = async () => {
    // 过滤出真正的新成员（排除已有成员）
    const newUserIds = Array.from(selectedNewUsers).filter(
      id => !existingMemberIds.has(id)
    );

    if (newUserIds.length === 0) {
      showToast(t('form.selectAtLeastOneMember'), 'warning');
      return;
    }

    setIsAddingMembers(true);
    try {
      const users = newUserIds.map(userId => ({
        userId: Number(userId),
        role: 'editor' as const,
      }));

      const response = await apiPost<any>(
        `/api/ai/conversations/${conversationId}/users`,
        { users }
      );

      if (response.code === 200) {
        showToast(t('groupChat.membersAddedSuccess'), 'success');
        setShowAddMemberModal(false);
        // 刷新成员列表
        loadMembers();
      } else {
        showToast(response.message || t('toast.addMembersFailed'), 'error');
      }
    } catch (error) {
      console.error('Error adding members:', error);
      showToast(t('toast.addMembersFailed'), 'error');
    } finally {
      setIsAddingMembers(false);
    }
  };

  // 🔥 获取头像首字母
  const getAvatarInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  // 🔥 获取头像背景色
  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-red-400',
      'bg-orange-400',
      'bg-yellow-400',
      'bg-green-400',
      'bg-blue-400',
      'bg-indigo-400',
      'bg-purple-400',
      'bg-pink-400',
      'bg-teal-400',
      'bg-cyan-400',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // 初始加载成员
  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // 监听在线用户 WebSocket 事件
  useEffect(() => {
    if (!isGroupChat || !conversationId) return;

    const handleOnlineUsers = (data: string | { users?: OnlineUser[] }) => {
      try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;

        // 检查是否是当前会话的在线用户更新
        if (
          parsed.conversationId &&
          String(parsed.conversationId) !== String(conversationId)
        ) {
          return;
        }

        const users: OnlineUser[] = parsed.users || parsed || [];
        const userIds = new Set(users.map((u: OnlineUser) => u.id));
        setOnlineUserIds(userIds);
      } catch (e) {
        console.error('Failed to parse online users:', e);
      }
    };

    // 监听 conversation_online_users 事件
    socketService.on('conversation_online_users', handleOnlineUsers);

    return () => {
      socketService.off('conversation_online_users', handleOnlineUsers);
    };
  }, [isGroupChat, conversationId]);

  // 非群聊不显示
  if (!isGroupChat) return null;

  // 统计在线人数
  const onlineCount = members.filter(m => onlineUserIds.has(m.user.id)).length;

  return (
    <div
      className={cn(
        'flex-shrink-0 border-l border-gray-200 bg-white transition-all duration-300 ease-in-out mt-10',
        isCollapsed ? 'w-12' : 'w-64'
      )}
    >
      {/* 折叠/展开按钮 */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className={cn(
          'w-full h-10 flex items-center  border-b border-gray-200 hover:bg-gray-50 transition-colors',
          isCollapsed ? 'justify-end pr-2' : 'justify-start pl-2'
        )}
        title={
          isCollapsed
            ? t('groupChat.expandMemberList')
            : t('groupChat.collapseMemberList')
        }
      >
        {isCollapsed ? (
          <ChevronLeft size={18} className="text-gray-500" />
        ) : (
          <ChevronRight size={18} className="text-gray-500" />
        )}
      </button>

      {isCollapsed ? (
        // 折叠状态：只显示图标和在线人数
        <div className="flex flex-col items-center py-3 gap-2">
          <Users size={20} className="text-gray-500" />
          <span className="text-xs text-gray-500">
            {onlineCount}/{members.length}
          </span>
        </div>
      ) : (
        // 展开状态：完整列表
        <div className="flex flex-col h-full">
          {/* 标题栏 */}
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  {t('groupChat.membersLabel')}
                </span>
              </div>
              <span className="text-xs text-gray-700">
                {onlineCount} {t('common.online')} / {members.length}
              </span>
            </div>
          </div>

          {/* 成员列表 */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
              </div>
            ) : members.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-sm text-gray-400">
                {t('groupChat.noMembersFound')}
              </div>
            ) : (
              <div className="py-2">
                {members.map(member => {
                  const isOnline = onlineUserIds.has(member.user.id);
                  // const config = roleConfig[member.role];
                  // const RoleIcon = config.icon;

                  return (
                    <div
                      key={member.user.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors"
                    >
                      {/* 头像 + 在线状态 */}
                      <div className="relative flex-shrink-0">
                        <div
                          className={cn(
                            'w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium',
                            isOnline ? 'bg-gray-600' : 'bg-gray-200'
                          )}
                        >
                          {getUserInitial(member.user)}
                        </div>
                        {/* 在线状态指示器 */}
                        <div
                          className={cn(
                            'absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white',
                            isOnline ? 'bg-green-500' : 'bg-gray-300'
                          )}
                        />
                      </div>

                      {/* 用户信息 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-gray-800 truncate">
                            {member.user.name ||
                              member.user.email?.split('@')[0] ||
                              t('common.user')}
                          </span>
                          {/* 🔥 Owner 皇冠图标 */}
                          {member.role === 'owner' && (
                            <Crown
                              size={14}
                              className="text-yellow-500 flex-shrink-0"
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-gray-400 truncate">
                            {member.user.email}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 🔥 Add Member 按钮 */}
            {projectId && (
              <div className="px-3 py-2 border-t border-gray-100 bg-white">
                <button
                  onClick={() => setShowAddMemberModal(true)}
                  className="flex items-center justify-center gap-2 w-full py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                >
                  <UserPlus size={16} />
                  <span>{t('groupChat.addMember')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 🔥 Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('project.addMembers')}
              </h3>
              <button
                onClick={() => setShowAddMemberModal(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Search */}
            <div className="px-4 py-3 border-b">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder={t('form.searchMembers')}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* User List */}
            <div className="flex-1 overflow-y-auto">
              {isLoadingProjectUsers ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
                  <span className="ml-2 text-sm text-gray-500">
                    {t('form.loadingProjectMembers')}
                  </span>
                </div>
              ) : projectUsers.length === 0 ? (
                <div className="p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                    <Users size={24} className="text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    {t('form.noOtherMembers')}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t('form.addMembersFirst')}
                  </p>
                </div>
              ) : filteredProjectUsers.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  {t('groupChat.noMatchingMembers')}
                </div>
              ) : (
                <div className="p-2">
                  {filteredProjectUsers.map(user => {
                    const isExistingMember = existingMemberIds.has(user.id);
                    const isSelected =
                      isExistingMember || selectedNewUsers.has(user.id);

                    return (
                      <button
                        key={user.id}
                        onClick={() => toggleUserSelection(user.id)}
                        disabled={isExistingMember}
                        className={cn(
                          'flex items-center gap-3 w-full text-left px-3 py-2 rounded-md transition-colors',
                          isExistingMember
                            ? 'bg-gray-50 cursor-not-allowed opacity-60'
                            : 'hover:bg-gray-50'
                        )}
                      >
                        {/* Avatar */}
                        <div
                          className={cn(
                            'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium',
                            getAvatarColor(user.name)
                          )}
                        >
                          {getAvatarInitials(user.name)}
                        </div>
                        {/* User info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {user.name}
                            </span>
                            {isExistingMember && (
                              <span className="text-xs text-gray-400">
                                {t('groupChat.alreadyInGroup')}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {user.email}
                          </div>
                        </div>
                        {/* Checkbox */}
                        <div
                          className={cn(
                            'w-4 h-4 border-2 rounded flex items-center justify-center transition-colors',
                            isSelected
                              ? 'border-blue-600 bg-blue-600'
                              : 'border-gray-300',
                            isExistingMember && 'opacity-50'
                          )}
                        >
                          {isSelected && (
                            <Check size={12} className="text-white" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
              <div className="text-sm text-gray-600">
                {selectedNewUsers.size === 1
                  ? t('groupChat.newMemberSelected')
                  : t('groupChat.newMembersSelected', {
                      count: selectedNewUsers.size,
                    })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddMemberModal(false)}
                  disabled={isAddingMembers}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors border border-gray-300 disabled:opacity-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleAddMembers}
                  disabled={isAddingMembers || selectedNewUsers.size === 0}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isAddingMembers && (
                    <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                  )}
                  {isAddingMembers ? t('project.adding') : t('project.add')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
